import { effect } from "@preact/signals";
import * as preact from "preact";

import * as deviceDetect from "deviceDetect";
import * as editCardDialog from "editCardDialog";
import * as dateUtils from "shared/dateUtils";
import * as languageMap from "shared/languageMap";
import * as sourceUtils from "shared/sourceUtils";
import * as utils from "shared/utils";
import RemoteUserWordsStore, {
  type UserWordsSearchQuery,
} from "stores/RemoteUserWordsStore";
import type { AppStores } from "stores/stores";
import UserWordStore, {
  formatContextSegments,
  type FormattedContextSegment,
  type UserWordData,
} from "stores/UserWordStore";
import * as tts from "tts";
import EditCardView from "views/EditCardView";
import SkeletonView from "views/SkeletonView";
import * as WordSheetView from "views/WordSheetView";

const allNbsp = new RegExp(String.fromCharCode(160), "g");

interface DisplayWord extends UserWordData {
  _id: string;
  date?: string;
  displayTranslations?: string;
  source?: sourceUtils.ContextSourceLink;
  wordWithContext?: FormattedContextSegment[];
}

interface RemoteWordListProps {
  model: RemoteUserWordsStore;
  stores: AppStores;
  wordGroup?: string;
  showCancelButton?: boolean;
  noWordsMessage?: string;
}

interface RemoteWordListState {
  audioWordID?: string;
  expandedWordID?: string;
  previousCheckboxChecked?: boolean;
  previousCheckboxID?: string;
}

const titleForWordGroup = (wordGroup?: string) => {
  if (!wordGroup) return undefined;
  const title = wordGroup.replace(/([A-Z])/g, " $1");
  return "Words " + title[0].toUpperCase() + title.substring(1);
};

/**
 * Show the first context from the text or source being filtered by, so a word
 * saved from several places links back to the one the filter matched.
 */
const calculateDisplayContext = (
  word: DisplayWord,
  { bookIDs, source }: UserWordsSearchQuery
) => {
  const context = word.contexts?.find(
    (candidate: any) =>
      candidate?.text &&
      (!bookIDs || candidate.bookID?._id === bookIDs) &&
      // A shelf's member sources are only known to the server
      (!source ||
        sourceUtils.isShelfSource(source) ||
        candidate.source === source)
  );

  if (!context) {
    return word;
  }

  const segments = formatContextSegments(context.text, word.word ?? "");
  const strongIndex = segments.findIndex(
    (segment) => segment.type === "strong"
  );

  if (strongIndex !== -1) {
    const before =
      strongIndex > 0 && segments[strongIndex - 1].type === "text"
        ? segments[strongIndex - 1].value
        : "";
    const after =
      strongIndex < segments.length - 1 &&
      segments[strongIndex + 1].type === "text"
        ? segments[strongIndex + 1].value
        : "";

    const trimmedBefore =
      before.length > 10
        ? "..." + before.substring(before.length - 10)
        : before;
    const trimmedAfter = after.length > 40 ? utils.elide(after, 40) : after;

    const trimmedSegments: FormattedContextSegment[] = [];
    if (trimmedBefore) {
      trimmedSegments.push({
        type: "text",
        value: trimmedBefore.replace(allNbsp, " "),
      });
    }
    trimmedSegments.push({
      type: "strong",
      value: segments[strongIndex].value.replace(allNbsp, " "),
    });
    if (trimmedAfter) {
      trimmedSegments.push({
        type: "text",
        value: trimmedAfter.replace(allNbsp, " "),
      });
    }

    word.wordWithContext = trimmedSegments;
  }

  word.source = sourceUtils.linkForContext(context);
  return word;
};

const eventTargetIsInteractive = (event: Event) => {
  const target = event.target;
  if (!(target instanceof Element)) return false;
  return Boolean(
    target.closest(
      ".checkboxCell, .checkboxLabel, input, button, a, .audioButton, .favorite, .externalLink"
    )
  );
};

export class RemoteWordList extends preact.Component<
  RemoteWordListProps,
  RemoteWordListState
> {
  private audioRequestSequence = 0;
  private disposeSignals?: () => void;
  /** Watches the end of the list, to load more words as it comes into view */
  private endObserver?: IntersectionObserver;
  private endVisible = false;

  state: RemoteWordListState = {};

  componentDidMount() {
    this.disposeSignals = effect(() => {
      this.props.model.viewJSON.value;
      this.props.model.selectionStore.itemIDs.value;
      this.props.model.selectionStore.wholeCollection.value;
      this.props.model.selectionStore.wholePage.value;
      deviceDetect.smallViewport.value;
      this.props.stores.user.firstLanguage.value;
      this.props.model.totalCountSignal.value;
      this.props.model.loadedSearchQuery.value;
      this.props.model.loadingMore.value;
      this.forceUpdate();
    });
    // Prime browser voices so the TTS column is shown for free users on
    // languages with device voices but no Azure entitlement.
    void tts
      .initialize(this.props.stores.user.currentLearningLanguage.value)
      .then(() => this.forceUpdate());
  }

  componentWillUnmount() {
    this.audioRequestSequence += 1;
    this.disposeSignals?.();
    this.endObserver?.disconnect();
  }

  componentDidUpdate() {
    // The observer only reports changes, so the end may still be in view
    // after loading more
    this.loadMoreIfEndVisible();
  }

  private observeEnd = (element: HTMLDivElement | null) => {
    this.endObserver?.disconnect();
    this.endVisible = false;
    if (!element) return;
    this.endObserver = new IntersectionObserver(
      ([entry]) => {
        this.endVisible = entry.isIntersecting;
        this.loadMoreIfEndVisible();
      },
      { rootMargin: "300px" }
    );
    this.endObserver.observe(element);
  };

  private loadMoreIfEndVisible() {
    if (this.endVisible) {
      this.props.model.fetchMore().catch(() => {
        // Already reported. Tries again once scrolled back into view.
        this.endVisible = false;
      });
    }
  }

  displayWords() {
    const searchQuery = this.props.model.loadedSearchQuery.value;
    return this.props.model.viewJSON.value.map((word) => {
      const userWord = this.props.model.getWord(String(word._id));
      const displayWord = calculateDisplayContext(
        { ...word, _id: String(word._id) },
        searchQuery
      );
      displayWord.date = dateUtils.format(
        dateUtils.dateFromObjectId(displayWord._id),
        "%YYYY/%MM/%DD"
      );
      displayWord.displayTranslations = userWord?.formatTranslations() ?? "";
      return displayWord;
    });
  }

  visibleWordIDs() {
    return this.displayWords().map((word) => word._id);
  }

  selected(wordID: string) {
    return this.props.model.selectionStore.itemIDs.value.includes(wordID);
  }

  clickCheckbox = (event: MouseEvent, wordID: string) => {
    event.stopPropagation();
    // Note: do NOT call event.preventDefault() here. Letting the browser's
    // native toggle complete keeps dom.checked in sync with the new selected
    // state, so Preact's `checked != dom[i]` diff (preact/src/diff/index.js)
    // does not skip the write after a canceled-activation revert — which
    // otherwise leaves the visible checkmark a click behind. See task 00314.

    const selectionStore = this.props.model.selectionStore;
    const visibleWordIDs = this.visibleWordIDs();
    let checked = !selectionStore.itemIDs.value.includes(wordID);
    let range: string[] | undefined;

    if (event.shiftKey && this.state.previousCheckboxID) {
      const index1 = visibleWordIDs.indexOf(wordID);
      const index2 = visibleWordIDs.indexOf(this.state.previousCheckboxID);

      if (index1 !== -1 && index2 !== -1) {
        range = visibleWordIDs.slice(
          Math.min(index1, index2),
          Math.max(index1, index2) + 1
        );
        checked = Boolean(this.state.previousCheckboxChecked);
      }
    }

    if (checked) {
      if (range) {
        selectionStore.addItems(range);
      } else {
        selectionStore.addItem(wordID);
      }
    } else if (range) {
      selectionStore.wholeCollection.value = false;
      selectionStore.wholePage.value = false;
      selectionStore.removeItems(range);
    } else {
      selectionStore.removeItem(wordID);
    }

    this.setState({
      previousCheckboxChecked: checked,
      previousCheckboxID: wordID,
    });
  };

  clickWordRow = (event: MouseEvent, wordID: string) => {
    if (eventTargetIsInteractive(event)) return;

    const userWord = this.props.model.getWord(wordID);
    if (!userWord) {
      throw new Error("user word not found");
    }

    if (deviceDetect.smallViewport.value) {
      // No room to open it within the list
      editCardDialog.show(userWord, {
        fullScreen: true,
        header: (
          <WordSheetView.WordSheetHeader
            userWord={userWord}
            user={this.props.stores.user}
          />
        ),
        playButton: true,
      });
      return;
    }
    this.setState({
      expandedWordID: this.state.expandedWordID === wordID ? undefined : wordID,
    });
  };

  clickAudio = (event: MouseEvent, userWord: UserWordStore) => {
    event.stopPropagation();
    const wordID = userWord.id;
    if (!wordID) return;

    const audioRequestID = this.audioRequestSequence + 1;
    this.audioRequestSequence = audioRequestID;
    this.setState({ audioWordID: wordID });
    const clearLoading = () => this.clearAudioLoading(audioRequestID);
    // Stops the spinner the moment audio starts, or can't
    void WordSheetView.playWord(userWord, this.props.stores.user, clearLoading);
  };

  clearAudioLoading(audioRequestID: number) {
    if (this.audioRequestSequence === audioRequestID) {
      this.setState({ audioWordID: undefined });
    }
  }

  clickFavorite = (event: MouseEvent, userWord: UserWordStore) => {
    event.stopPropagation();
    void userWord.toggleFavorite();
  };

  clickExternalLink = (event: MouseEvent) => {
    event.stopPropagation();
  };

  clickDelete = () => {
    const checkedUserWords = this.props.model.selectionStore.itemIDs.value
      .map((wordID) => this.props.model.getWord(wordID))
      .filter(Boolean) as UserWordStore[];

    if (checkedUserWords.length === 0) {
      console.error("no selected elements");
      return;
    }

    const wordsString =
      checkedUserWords.length === 1
        ? "word"
        : checkedUserWords.length + " words";

    if (
      confirm(
        "Are you sure you want to delete the selected " + wordsString + "?"
      )
    ) {
      for (const userWord of checkedUserWords) {
        userWord.deleteUserWord();
      }
    }
  };

  renderWordRows(
    word: DisplayWord,
    ttsSupported: boolean,
    rtl: boolean,
    selected: boolean
  ) {
    const userWord = this.props.model.getWord(word._id);
    if (!userWord) return null;

    const audioClass =
      this.state.audioWordID === word._id
        ? "icon-spinner icon-spin audioButton needsclick"
        : "icon-volume-down audioButton needsclick";

    return (
      <preact.Fragment key={word._id}>
        <tr
          class={`clickable wordRow${selected ? " selected" : ""}`}
          data-userWordId={word._id}
          onClick={(event) => this.clickWordRow(event, word._id)}
        >
          <td style="padding:0 0 0 2px">
            <div class="checkboxCell">
              <input
                checked={selected}
                data-userWordId={word._id}
                id={`checkbox-${word._id}`}
                onChange={() => undefined}
                onClick={(event) => this.clickCheckbox(event, word._id)}
                type="checkbox"
              />
              <label
                class="checkboxLabel"
                for={`checkbox-${word._id}`}
                onClick={(event) => this.clickCheckbox(event, word._id)}
              >
                <i class="icon-check"></i>
                <i class="icon-check-empty"></i>
                <i class="icon-check-minus"></i>
              </label>
            </div>
          </td>
          {ttsSupported ? (
            <td class="noMobile" style="height:29px">
              <i
                title="Pronounce"
                class={audioClass}
                onClick={(event) => this.clickAudio(event, userWord)}
              ></i>
            </td>
          ) : null}
          <td class="noMobile">
            <span
              title="Favorite"
              class={`favorite${word.favorite ? " selected" : ""}`}
              onClick={(event) => this.clickFavorite(event, userWord)}
            ></span>
          </td>
          <td
            class="wordColumn"
            id={`word-${word._id}`}
            dir={rtl ? "rtl" : undefined}
          >
            {word.wordWithContext ? (
              <span>
                {word.wordWithContext.map((segment, index) =>
                  segment.type === "strong" ? (
                    <strong key={index}>{segment.value}</strong>
                  ) : (
                    <preact.Fragment key={index}>
                      {segment.value}
                    </preact.Fragment>
                  )
                )}
              </span>
            ) : (
              <strong>{word.word}</strong>
            )}
          </td>
          <td id={`translation-${word._id}`} class="translationColumn">
            {word.displayTranslations}
          </td>
          <td id={`source-${word._id}`} class="rightPadding noPhablet">
            {word.source ? (
              <a
                target="_blank"
                href={word.source.href}
                class="externalLink"
                onClick={this.clickExternalLink}
              >
                {word.source.title}
              </a>
            ) : null}
          </td>
        </tr>
        {this.state.expandedWordID === word._id ? (
          <tr class="editCardPanelRow" data-userWordId={word._id}>
            <td colSpan={6}>
              <EditCardView
                userWord={userWord}
                options={{}}
                phrase={/ /.test(userWord.state.value.word)}
              />
            </td>
          </tr>
        ) : null}
      </preact.Fragment>
    );
  }

  render() {
    const words = this.displayWords();
    const languageInfo =
      languageMap.map[this.props.stores.user.currentLearningLanguage.value];
    const ttsSupported =
      tts.getAvailabilitySignal({
        user: this.props.stores.user,
        feature: "speakingMode",
        language: this.props.stores.user.currentLearningLanguage.value,
      }).value === "supported";
    const rtl = languageInfo.direction === "RTL";
    const title = titleForWordGroup(this.props.wordGroup);

    return (
      <>
        {title ? <h2>{title} &nbsp; &nbsp; &nbsp;</h2> : null}
        {words.length > 0 ? (
          <table
            class={utils.cn({
              wordTable: true,
              basicLatinFirstLanguage: languageMap.writtenInBasicLatin(
                this.props.stores.user.firstLanguage.value
              ),
            })}
          >
            <colgroup>
              <col class="columnSelectCheckbox" />
              {ttsSupported ? <col class="columnTTS noMobile" /> : null}
              <col class="columnFavorite noMobile" />
              <col class="columnWord" />
              <col class="columnTranslation" />
            </colgroup>
            <tbody>
              {words.map((word) =>
                this.renderWordRows(
                  word,
                  ttsSupported,
                  rtl,
                  this.selected(word._id)
                )
              )}
            </tbody>
          </table>
        ) : (
          <p class="noWordsMessage">
            {this.props.noWordsMessage ?? "There are no words here."}
          </p>
        )}
        <div class="wordListEnd" ref={this.observeEnd}>
          {this.props.model.loadingMore.value ? (
            <SkeletonView kind="rows" count={2} />
          ) : null}
        </div>
      </>
    );
  }
}
